import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * CartProgress — overlay shown during batch add-to-cart operations.
 *
 * Listens to 'cart:progress' CustomEvent dispatched by useExporter.
 * Shows a centered overlay with progress bar and status message.
 *
 * Phases:
 *   saving_design       → "Saving design..."
 *   adding_to_cart       → "Adding N items to cart..."
 *   generating_previews  → "Generating previews..."
 *   complete             → "Done! X items added to cart" (auto-dismisses)
 *   error                → "Error: ..." (stays until dismissed)
 */

interface ProgressState {
  visible: boolean;
  phase: string;
  message: string;
  current: number;
  total: number;
}

const initialState: ProgressState = {
  visible: false,
  phase: '',
  message: '',
  current: 0,
  total: 0,
};

function CartProgress() {
  const [state, setState] = useState<ProgressState>(initialState);

  useEffect(() => {
    const handleProgress = (e: CustomEvent) => {
      const { phase, message, current, total } = e.detail || {};

      if (phase === 'complete') {
        setState({
          visible: true,
          phase,
          message: message || 'Items added to cart!',
          current: current || 0,
          total: total || 0,
        });
        setTimeout(() => {
          setState(initialState);
        }, 2500);
        return;
      }

      if (phase === 'error') {
        setState({
          visible: true,
          phase,
          message: message || 'An error occurred',
          current: 0,
          total: 0,
        });
        setTimeout(() => setState(initialState), 5000);
        return;
      }

      setState({
        visible: true,
        phase,
        message: message || '',
        current: current || 0,
        total: total || 0,
      });
    };

    window.addEventListener('cart:progress', handleProgress as EventListener);
    return () => {
      window.removeEventListener('cart:progress', handleProgress as EventListener);
    };
  }, []);

  if (!state.visible) return null;

  const progressPercent = (() => {
    if (state.phase === 'saving_design') return 20;
    if (state.phase === 'adding_to_cart') {
      if (state.total > 0) return 20 + (state.current / state.total) * 60;
      return 50;
    }
    if (state.phase === 'generating_previews') return 10;
    if (state.phase === 'complete') return 100;
    if (state.phase === 'error') return 0;
    return 0;
  })();

  const phaseIcon = (() => {
    if (state.phase === 'complete') return '\u2713'; // checkmark
    if (state.phase === 'error') return '\u2717';    // X mark
    return '';
  })();

  return (
    <Overlay>
      <Card isError={state.phase === 'error'} isComplete={state.phase === 'complete'}>
        {(state.phase !== 'complete' && state.phase !== 'error') && (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        )}

        {phaseIcon && (
          <IconContainer isError={state.phase === 'error'}>
            {phaseIcon}
          </IconContainer>
        )}

        <Message>{state.message}</Message>

        {(state.phase !== 'complete' && state.phase !== 'error') && (
          <ProgressBarContainer>
            <ProgressBarFill style={{ width: `${progressPercent}%` }} />
          </ProgressBarContainer>
        )}
      </Card>
    </Overlay>
  );
}

export default React.memo(CartProgress);

// --- Styled Components ---

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Card = styled.div<{ isError?: boolean; isComplete?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 32px 40px;
  min-width: 320px;
  max-width: 440px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  ${({ isError }) => isError && `
    border: 2px solid #e53e3e;
  `}

  ${({ isComplete }) => isComplete && `
    border: 2px solid #38a169;
  `}
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #2d3748;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const IconContainer = styled.div<{ isError?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  color: white;
  background: ${({ isError }) => isError ? '#e53e3e' : '#38a169'};
`;

const Message = styled.p`
  font-size: 15px;
  color: #2d3748;
  margin: 0;
  font-family: 'Work Sans', 'Nunito Sans', sans-serif;
  line-height: 1.4;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
`;

const progressAnimation = keyframes`
  from { background-position: 0 0; }
  to { background-position: 30px 0; }
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: #2d3748;
  border-radius: 3px;
  transition: width 0.3s ease-out;
  background-image: linear-gradient(
    45deg,
    rgba(255,255,255,0.15) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255,255,255,0.15) 50%,
    rgba(255,255,255,0.15) 75%,
    transparent 75%,
    transparent
  );
  background-size: 30px 30px;
  animation: ${progressAnimation} 1s linear infinite;
`;
